import React, { useState, useCallback, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { DataTable } from '../ui/data-table';

const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const BUCKET_CONFIG = {
  '0-30': { label: '0–30 jours', color: 'bg-muted border-border', textColor: 'text-foreground' },
  '31-60': { label: '31–60 jours', color: 'bg-amber-50 border-amber-200', textColor: 'text-amber-700' },
  '61-90': { label: '61–90 jours', color: 'bg-orange-50 border-orange-200', textColor: 'text-orange-700' },
  '90+': { label: '90+ jours', color: 'bg-red-50 border-red-200', textColor: 'text-red-700' },
};

const DebtAgingCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/finance/debt-aging');
      setData(res.data);
    } catch {
      setError('Impossible de charger les données de créances.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  const buckets = data?.buckets || {};
  const bucketCounts = data?.bucket_counts || {};
  const invoices = data?.invoices || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {data?.invoice_count || 0} factures impayées — Total : <strong className="text-foreground">{fmt(data?.total_unpaid)}</strong>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw size={14} className="mr-1" />
          Actualiser
        </Button>
      </div>

      {/* Aging bucket cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => (
          <Card key={key} className={`border ${cfg.color}`}>
            <CardContent className="pt-4 pb-3 px-4 text-center">
              <div className="text-xs text-muted-foreground mb-1">{cfg.label}</div>
              <div className={`text-lg font-bold ${cfg.textColor}`}>{fmt(buckets[key] || 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">{bucketCounts[key] || 0} factures</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Proportional bar */}
      {data?.total_unpaid > 0 && (
        <div className="flex h-3 rounded-full overflow-hidden bg-muted">
          {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => {
            const amount = buckets[key] || 0;
            const pct = (amount / data.total_unpaid) * 100;
            if (pct === 0) return null;
            const barColorMap = { '0-30': '#94a3b8', '31-60': '#f59e0b', '61-90': '#ea580c', '90+': '#ef4444' };
            return (
              <div
                key={key}
                className="h-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: barColorMap[key] }}
                title={`${cfg.label}: ${fmt(amount)} (${pct.toFixed(1)}%)`}
              />
            );
          })}
        </div>
      )}

      {/* Invoice table */}
      {invoices.length > 0 && (
        <DataTable
          data={invoices}
          searchPlaceholder="Rechercher facture, client..."
          emptyMessage="Aucune facture impayée"
          pageSize={10}
          columns={[
            {
              accessorKey: 'invoice_number',
              header: 'N° Facture',
              meta: { cellClassName: 'font-mono text-xs' },
            },
            {
              accessorKey: 'client_name',
              header: 'Client',
              meta: { cellClassName: 'font-medium text-foreground' },
            },
            {
              accessorKey: 'amount_remaining',
              header: 'Reste dû',
              cell: ({ getValue }) => (
                <span className="font-semibold text-red-600">{fmt(getValue())}</span>
              ),
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorKey: 'days_outstanding',
              header: 'Jours',
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorKey: 'bucket',
              header: 'Tranche',
              cell: ({ getValue }) => {
                const b = getValue();
                const cfg = BUCKET_CONFIG[b];
                return (
                  <Badge variant="outline" className={`text-[10px] ${cfg?.color} ${cfg?.textColor}`}>
                    {cfg?.label || b}
                  </Badge>
                );
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default DebtAgingCard;
